/**
 * VictoryModal Component
 *
 * Displays the game end state (victory or defeat) modal.
 * Integrates prize wheel when winner captured the bonus piece.
 * Prize wheel includes all prizes from both players in the pool.
 *
 * Props:
 * - isOpen: Boolean indicating if modal is visible
 * - didIWin: Boolean indicating if current player won
 * - onClose: Callback when modal is dismissed
 * - myAttacks: Array of attacks (for checking bonus piece capture)
 * - myPrizes: Array of winner's prizes (combined with opponent's for prize wheel)
 * - onSendPrizeSelection: Callback to send prize selection to server
 * - hasCapturedBonusPiece: Boolean indicating if winner captured the bonus piece
 */

import { useEffect, useState, useMemo } from 'react';
import { useGameStore } from '../stores/gameStore';
import { sendMessage } from '../services/partyService';
import PrizeWheel from './PrizeWheel';
import {
  shouldShowPrizeWheel,
  selectRandomPrize,
} from '../utils/prizeWheelLogic';
import { COPY } from '../copy';

export default function VictoryModal({
  isOpen = false,
  didIWin = false,
  onClose,
  myAttacks = [],
  myPrizes = [],
  onSendPrizeSelection = null,
  hasCapturedBonusPiece = false,
}) {
  const {
    showPrizeWheel,
    prizeWheelSpinning,
    selectedPrize,
    startPrizeWheelSpin,
    completePrizeWheelSpin,
    opponentPrizes,
  } = useGameStore();
  const [hasInitiatedPrizeWheel, setHasInitiatedPrizeWheel] = useState(false);
  const [waitingForSpin, setWaitingForSpin] = useState(true);

  // Combine all prizes from both players for the prize pool
  // Use useMemo to prevent infinite re-renders
  const allPrizes = useMemo(
    () => [...(myPrizes || []), ...(opponentPrizes || [])],
    [myPrizes, opponentPrizes]
  );

  // Check if we should show prize wheel
  // Prize wheel shows for BOTH players if winner captured the bonus piece AND there are any prizes
  // Memoize to prevent unnecessary re-renders
  const shouldShowWheel = useMemo(
    () => hasCapturedBonusPiece && allPrizes.length > 0,
    [hasCapturedBonusPiece, allPrizes]
  );

  // Handle spin button click (winner only)
  const handleSpinClick = async () => {
    if (!hasInitiatedPrizeWheel) {
      setHasInitiatedPrizeWheel(true);
      setWaitingForSpin(false);

      // Send PRIZE_SPIN_STARTED message to server
      // Server will broadcast to both players, triggering spin animation
      await sendMessage({ type: 'PRIZE_SPIN_STARTED' });

      // Select random prize after 5 seconds from all available prizes
      setTimeout(() => {
        const prize = selectRandomPrize(allPrizes);
        if (prize) {
          completePrizeWheelSpin(prize);
          // Send selection to server
          if (onSendPrizeSelection) {
            onSendPrizeSelection(prize);
          }
        }
      }, 5000);
    }
  };

  // When prize wheel spinning starts (via PRIZE_SPIN_STARTED broadcast), exit waiting state
  useEffect(() => {
    if (prizeWheelSpinning && waitingForSpin) {
      setWaitingForSpin(false);
    }
  }, [prizeWheelSpinning, waitingForSpin]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitiatedPrizeWheel(false);
      setWaitingForSpin(true);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // Show prize wheel if conditions met
  if (shouldShowWheel) {
    return (
      <PrizeWheel
        prizes={allPrizes}
        isSpinning={prizeWheelSpinning && !waitingForSpin}
        selectedPrize={selectedPrize}
        waitingForSpin={waitingForSpin}
        isWinner={didIWin}
        onSpinClick={handleSpinClick}
      />
    );
  }

  // Show victory/defeat modal
  return (
    <div
      data-testid="victory-modal"
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="victory-modal-title"
    >
      <div className="bg-dark-charcoal border-2 border-neon-purple shadow-glow-purple rounded-lg p-8 max-w-md w-full mx-4">
        <h2
          id="victory-modal-title"
          className={`text-3xl font-bold mb-4 text-center ${
            didIWin
              ? 'text-neon-green text-glow-cyan'
              : 'text-neon-pink text-glow-pink'
          }`}
        >
          {didIWin ? COPY.modal.victory.titleWin : COPY.modal.victory.titleLose}
        </h2>

        <div className="text-center mb-6">
          <p className="text-lg text-gray-200">
            {didIWin
              ? hasCapturedBonusPiece
                ? COPY.modal.victory.messageWinWithBonus
                : COPY.modal.victory.messageWin
              : hasCapturedBonusPiece
                ? COPY.modal.victory.messageLoseWithBonus
                : COPY.modal.victory.messageLose}
          </p>
          {didIWin && !hasCapturedBonusPiece && (
            <p className="text-sm text-gray-400 mt-2">
              (No prize wheel - bonus piece not captured)
            </p>
          )}
        </div>

        {/* Show selected prize if prize wheel was shown */}
        {selectedPrize && (
          <div className="mb-6 p-4 bg-gradient-to-r from-neon-yellow to-neon-coral border-2 border-neon-yellow rounded-lg text-center shadow-glow-yellow">
            <p className="text-dark-navy text-sm mb-1 font-semibold">
              {COPY.modal.victory.prizeWheelResult}
            </p>
            <p className="text-dark-navy text-xl font-bold">{selectedPrize}</p>
          </div>
        )}

        <div className="flex justify-center gap-4">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-neon-cyan border-2 border-white/70 text-dark-navy rounded-lg hover:bg-neon-pink hover:border-white/70 hover:text-white transition-all shadow-glow-cyan"
          >
            {COPY.modal.victory.buttons.close}
          </button>
          <a
            href="/"
            className="px-6 py-2 bg-dark-purple border-2 border-white/70 text-gray-100 rounded-lg hover:bg-neon-purple hover:text-white transition-all shadow-glow-purple"
          >
            {COPY.modal.victory.buttons.newGame}
          </a>
        </div>
      </div>
    </div>
  );
}
